import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.title = '关于我们 - 黄油游戏库';
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="px-4 md:px-6 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-heading text-foreground-900 mb-6">关于黄油游戏库</h1>
            <p className="text-lg text-foreground-600 leading-relaxed max-w-3xl mx-auto">
              黄油游戏库诞生于一群独立游戏爱好者的热情。我们相信，好的游戏不需要华丽的预算和庞大的团队——最纯粹的游戏乐趣，往往来自那些充满创意和心血的独立作品。
            </p>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-6">我们的故事</h2>
                <div className="space-y-4 text-foreground-700 leading-relaxed">
                  <p>
                    黄油游戏库创立于2024年，由一群热爱独立游戏的开发者和玩家共同创建。我们深知独立游戏开发者的不易，也理解玩家寻找优质独立游戏的困难。因此，我们决定打造一个专注于独立游戏的精选平台。
                  </p>
                  <p>
                    我们相信每一款独立游戏都有它独特的魅力——可能是令人深思的故事，可能是创新的玩法机制，可能是精美的艺术风格。我们的使命就是帮助这些优秀的作品被更多玩家发现和欣赏。
                  </p>
                  <p>
                    目前，黄油游戏库已经收录了超过2000款精选独立游戏，拥有50万+活跃玩家社区。我们与数百位独立游戏开发者建立了紧密的合作关系，确保平台上的每一款游戏都经过精心挑选。
                  </p>
                </div>
              </div>
              <div className="relative rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Cozy%20creative%20team%20working%20on%20indie%20game%20project%2C%20warm%20studio%20lighting%2C%20artistic%20workspace%20with%20sketches%20and%20monitors%2C%20friendly%20collaborative%20atmosphere%2C%20modern%20industrial%20office%2C%20soft%20warm%20tones%2C%20creative%20energy&width=800&height=600&seq=about-story-image&orientation=landscape"
                  alt="黄油游戏库团队"
                  title="黄油游戏库 - 我们的故事"
                  className="w-full aspect-[4/3] object-cover object-top rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">品牌口碑</h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                来自玩家和开发者的真实声音，是我们不断前行的动力
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              {[
                {
                  quote: '"黄油游戏库是我发现独立游戏的首选平台，每次打开都能找到让我惊喜的新游戏。推荐系统真的很懂我！"',
                  author: '游戏玩家张先生',
                  role: '资深独立游戏爱好者',
                },
                {
                  quote: '"作为独立游戏开发者，黄油游戏库给了我们一个展示作品的绝佳舞台。平台的社区氛围很好，玩家的反馈也非常有建设性。"',
                  author: '李开发',
                  role: '独立游戏开发者',
                },
                {
                  quote: '"我是被朋友推荐过来的，没想到在这里发现了这么多宝藏游戏。游戏评测文章质量很高，帮助我节省了不少选择的时间。"',
                  author: '王同学',
                  role: '大学游戏社团成员',
                },
              ].map((item, i) => (
                <div key={i} className="bg-background-100 rounded-lg border border-background-200/70 p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <div key={j} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-primary-500 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    {item.quote}
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent-200 flex items-center justify-center">
                      <i className="ri-user-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{item.author}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              {[
                { value: '98%', label: '用户满意度' },
                { value: '2000+', label: '平台游戏数' },
                { value: '500+', label: '合作开发者' },
                { value: '4.8', label: '平均评分' },
              ].map((stat, i) => (
                <div key={i} className="text-center p-5 bg-background-100 rounded-lg border border-background-200/70">
                  <div className="text-2xl md:text-3xl font-bold text-primary-600 mb-1">{stat.value}</div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">联系我们</h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                有任何问题、建议或合作意向，欢迎随时联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
              {[
                {
                  icon: 'ri-mail-line',
                  title: '电子邮箱',
                  content: 'support@huangyougame.com',
                  sub: '商务合作：biz@huangyougame.com',
                },
                {
                  icon: 'ri-discuss-line',
                  title: '官方社群',
                  content: 'QQ群：123456789',
                  sub: '微信群：huangyougame',
                },
                {
                  icon: 'ri-map-pin-line',
                  title: '公司地址',
                  content: '上海市浦东新区',
                  sub: '张江高科技园区',
                },
              ].map((item, i) => (
                <div key={i} className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-700">{item.content}</p>
                  <p className="text-xs text-foreground-500 mt-1">{item.sub}</p>
                </div>
              ))}
            </div>

            <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
              <h3 className="text-lg font-semibold text-foreground-900 mb-1">工作时间</h3>
              <p className="text-sm text-foreground-600 mb-4">
                我们的客服团队在工作时间内随时为您提供帮助
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-background-100 rounded-lg">
                  <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">工作日</p>
                    <p className="text-xs text-foreground-500">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-background-100 rounded-lg">
                  <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">周末及节假日</p>
                    <p className="text-xs text-foreground-500">10:00 - 17:00</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于黄油游戏库',
          description: '黄油游戏库是专注于独立游戏的精选聚合平台，为独立游戏爱好者提供最优质的游戏发现和内容体验。',
          url: 'https://www.huangyougame.com/about',
          mainEntity: {
            '@type': 'Organization',
            name: '黄油游戏库',
            url: 'https://www.huangyougame.com',
            description: '精选独立游戏一站式畅玩平台',
            foundingDate: '2024',
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              email: 'support@huangyougame.com',
              availableLanguage: 'Chinese',
            },
            address: {
              '@type': 'PostalAddress',
              addressLocality: '上海市',
              addressRegion: '浦东新区',
              addressCountry: 'CN',
            },
          },
        })}
      </script>
    </>
  );
}